import moment from "moment";

import "./HomeTitle.css";

/**
 * Calculates the progress percentage towards the next ice age.
 * The progress bar goes from 0% (2 days remaining) to 100% (0 days remaining).
 * @param {string} iceAgeDateString - The ISO 8601 string of the next ice age date.
 * @returns {number} The calculated progress percentage.
 */
function calculateProgressFromDate(iceAgeDateString) {
  const PERIOD_IN_DAYS = 2;

  // Convert the string to a Date object and get the current time.
  const iceAgeDate = new Date(iceAgeDateString);
  const now = new Date();

  // Calculate the difference in days.
  const diffInMs = iceAgeDate.getTime() - now.getTime();
  const timeRemaining = diffInMs / (1000 * 60 * 60 * 24);

  // Calculate the elapsed time in days within the current cycle.
  const timeElapsed = PERIOD_IN_DAYS - timeRemaining;

  // Handle edge cases where the time is outside the 2-day period.
  if (timeElapsed < 0) {
    return 0;
  }
  if (timeElapsed > PERIOD_IN_DAYS) {
    return 100;
  }

  // Calculate and return the percentage.
  const percentage = (timeElapsed / PERIOD_IN_DAYS) * 100;
  return parseFloat(percentage.toFixed(2));
}

const StatusCard = ({ label, value, progress, caption }) => (
  <div className="home-status-card">
    <div className="home-status-row">
      <div className="home-status-label">
        {label}
        {caption && <span className="home-status-caption">{caption}</span>}
      </div>
      <div className="home-status-value">{value}</div>
    </div>
    <div
      className="home-status-bar"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
    >
      <div className="home-status-bar-fill" style={{ width: `${progress}%` }} />
    </div>
  </div>
);

function HomeTitle(props) {
  const {
    hourDouble,
    mobDeaths = 1704093874,
    iceAge = "",
    online,
    shs,
    imms,
    helpers,
    loading
  } = props;

  const show = (value) => (loading || value == null ? "—" : value);

  const mobsLeft = 1000000 - (mobDeaths % 1000000);
  const doubleProgress = 100 - (mobsLeft / 1000000) * 100;
  const iceAgeProgress = calculateProgressFromDate(iceAge);

  const hasDouble = !!hourDouble;
  const hasIceAge = !!iceAge && moment(iceAge).isValid();

  return (
    <div className="home-status">
      <div className="home-status-card">
        <div className="home-status-row">
          <div className="home-status-label">Online</div>
          <div className="home-status-value">
            {show(online)}
          </div>
        </div>
        <div className="home-status-staff">
          <span className="home-staff--sh">{show(shs)} SHs</span>
          <span className="home-staff--imm">{show(imms)} IMMs</span>
          <span className="home-staff--helper">{show(helpers)} Helpers</span>
        </div>
      </div>
      <StatusCard
        label="Hour Double"
        value={hasDouble ? moment(hourDouble).fromNow() : "—"}
        progress={doubleProgress}
        caption={`${mobsLeft.toLocaleString()} mobs left`}
      />
      <StatusCard
        label="Ice Age"
        value={hasIceAge ? moment(iceAge).format("dddd") : "—"}
        progress={hasIceAge ? iceAgeProgress : 0}
        caption={hasIceAge ? `in ${moment(iceAge).toNow(true)}` : ""}
      />
    </div>
  );
}

export default HomeTitle;
